// acts as a home page arranges the components in a grid layout
import {
  Button,
  Row,
  Col,
  Typography,
  Tooltip,
  Popconfirm,
  Divider,
  notification,
  Spin,
  Modal,
  Input,
  Dropdown
} from "antd";
import {
  TeamOutlined,
  SaveOutlined,
  PrinterOutlined,
  SlidersOutlined,
  ContainerOutlined,
  FileAddOutlined,
  ArrowLeftOutlined,
  DiffOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import axiosInstance from "utilities/axios";

import ProductDisplay from "./ProductDisplay";
import CustomerDetails from "./CustomerDetails";
import PastEstimatesModal from "./PastEstimates";
import PDFViewerModal from "./PrintModal";
import {
  createEstimate,
  newEstimate,
  saveEstimate,
  setClinicData,
  setCustomerData
} from "utilities/redux/slices/currentEstimate";
import useRoleCheck from "utilities/hooks/roleCheck";
import { fetchEstimates } from "utilities/redux/slices/estimatesSlice";
import { fetchTemplates } from "utilities/redux/slices/templatesSlice";
import config from "config";

const { Text, Title } = Typography;


const Calculator = () => {
  const { hasPermission } = useRoleCheck();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const roles = useSelector((state) => state.auth.data.roles);
  const clinic = useSelector((state) => state.currentEstimate.clinicData.clinicNo);
  const province = useSelector((state) => state.currentEstimate.clinicData.province);
  const programEligability = useSelector((state) => state.currentEstimate.customerData.programEligability);
  const estimate = useSelector((state) => state.currentEstimate);

  const [openModal, setOpenModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");

  const handlePrint = ({ black_and_white = false } = {}) => {
    if (estimate.products?.length === 0) {
      notification.warning({
        message: "Warning",
        description: "Cannot print an empty estimate.",
        placement: "bottomRight",
      });
      return;
    }

    setPdfLoading(true);
    axiosInstance
      .get(`/estimates/${estimate.id}/print`, {
        params: {
          black_and_white,
        },
      })
      .then((response) => {
        setPdfUrl(response.data.pdf_url);
        setPdfLoading(false);
      })
      .catch((error) => {
        console.error("Error loading estimate PDF:", error);
        notification.error({
          message: "Error",
          description: "Failed to load the estimate.",
        });
      })
      .finally(() => {
        setPdfLoading(false);
      });
  };

  const items = [
    {
      key: "color",
      label: (
        <Button
          type="text"
          onClick={() => handlePrint({ black_and_white: false })}
          style={{ width: "100%", textAlign: "left" }}
        >
          Print Color
        </Button>
      ),
    },
    {
      key: "black_and_white",
      label: (
        <Button
          type="text"
          onClick={() => handlePrint({ black_and_white: true })}
          style={{ width: "100%", textAlign: "left" }}
        >
          Print Black & White
        </Button>
      ),
    }
  ];

  const handleEmail = () => {
    // Returns a promise for email sending logic.
    return axiosInstance
      .post(`/estimates/${estimate.id}/email`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.error("Error sending email:", error);
        throw error;
      });
  };

  // if (!roles || roles.length === 0) {
  //   return (
  //     <div style={{
  //       display: "flex",
  //       justifyContent: "center",
  //       alignItems: "center",
  //       height: "100vh",
  //     }}>
  //       <Spin />
  //     </div>
  //   );
  // }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <Row gutter={[16, 16]} justify="space-between" style={{ width: "100%", marginLeft: 16 }}>
        <Col>
          <Row align="middle" gutter={16}>
            <Tooltip title="Back to Clinic Central">
              <Button
                type="text"
                onClick={() => window.location.href = config.clinicCentralUrl}
                style={{ padding: 0, margin: 0 }}
              >
                <Text type="secondary" style={{ fontSize: "30px" }}>
                  <ArrowLeftOutlined />
                </Text>
              </Button>
            </Tooltip>
            <img
              src="/HLBubble.svg"
              alt="logo"
              style={{ maxHeight: "60px", marginRight: "0.5rem", marginLeft: "1rem" }}
            />
            <Title level={3} style={{ margin: 0 }}>Product Comparison</Title>
          </Row>
        </Col>
        <Col>
          <Row gutter={[16, 16]} justify="space-around" align="middle" style={{ marginRight: 16 }}>
            <Col>
              <Tooltip title="View Past Estimates and Templates">
                <Button
                  size="large"
                  type="text"
                  icon={
                    <Text type="secondary" style={{ fontSize: "45px" }}>
                      <ContainerOutlined />
                    </Text>
                  }
                  onClick={() => setOpenModal(true)}
                />
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title="Start New Estimate">
                <Popconfirm
                  title="Start New Estimate"
                  description={
                    <>
                      Are you sure you want to start a new estimate?
                      <br />
                      This will clear the current estimate, but it will be saved.
                    </>
                  }
                  onConfirm={() => {
                    dispatch(newEstimate());

                    dispatch(setClinicData({
                      clinicNo: clinic,
                      province: province,
                    }));
              
                    dispatch(setCustomerData({
                      name: null,
                      email: null,
                      programEligability: programEligability,
                      financingOption: null,
                      downPayment: null,
                    }));
              
                    dispatch(createEstimate());
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    size="large"
                    type="text"
                    icon={
                      <Text type="secondary" style={{ fontSize: "45px" }}>
                        <FileAddOutlined />
                      </Text>
                    }
                  />
                </Popconfirm>
              </Tooltip>
            </Col>
            <Col>
              <Divider type="vertical" style={{ height: "45px" }} />
            </Col>
            <Col>
              <Tooltip title="Save Estimate as Template">
                <Button
                  size="large"
                  type="text"
                  icon={
                    <Text type="secondary" style={{ fontSize: "45px" }}>
                      <DiffOutlined />
                    </Text>
                  }
                  disabled={estimate.status !== "succeeded"}
                  onClick={() => {
                    if (estimate.products?.length === 0) {
                      notification.warning({
                        message: "Warning",
                        description: "Cannot save an empty estimate as a template.",
                        placement: "bottomRight",
                      });
                      return;
                    }

                    setShowTemplateModal(true);
                  }}
                />
              </Tooltip>
            </Col>
            {/* <Col span={1}>
              <Tooltip title="Save">
                <Button
                  size="large"
                  type="text"
                  icon={
                    <Text type="secondary" style={{ fontSize: "45px" }}>
                      <SaveOutlined />
                    </Text>
                  }
                  onClick={() => dispatch(saveEstimate())}
                />
              </Tooltip>
            </Col> */}
            <Col>
              <Dropdown
                menu={{ items }}
                placement="bottom"
                disabled={estimate.status !== "succeeded" || pdfLoading}
                loading={pdfLoading}
              >
                <Button
                  size="large"
                  type="text"
                  disabled={estimate.status !== "succeeded"}
                  loading={pdfLoading}
                  style={{ padding: 0, margin: 0 }}
                  icon={
                    <Text type="secondary" style={{ fontSize: "45px" }}>
                      <PrinterOutlined />
                    </Text>
                  }
                />
              </Dropdown>
            </Col>
            <Col>
              <Divider type="vertical" style={{ height: "45px" }} />
            </Col>
            <Col>
              <Tooltip title="Configure Customer Details">
                <Button
                  size="large"
                  type="text"
                  icon={
                    <Text type="secondary" style={{ fontSize: "45px" }}>
                      <TeamOutlined />
                    </Text>
                  }
                  onClick={() => {
                    setShowCustomerDetails(true);
                  }}
                />
              </Tooltip>
            </Col>
            {hasPermission(["Admin.Manage", "Marketing", "Sales.Exec", "BP.Admin"]) && (
              <>
                <Col>
                  <Divider type="vertical" style={{ height: "45px" }} />
                </Col>
                <Col>
                  <Tooltip title="Admin Page">
                    <Button
                      size="large"
                      type="text"
                      icon={
                        <Text type="secondary" style={{ fontSize: "45px" }}>
                          <SlidersOutlined />
                        </Text>
                      }
                      onClick={() => navigate("/marketing/affinity-partners")}
                    />
                  </Tooltip>
                </Col>
              </>
            )}
          </Row>
        </Col>
      </Row>
      <Row
        gutter={[16, 16]}
        justify="end"
        style={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        <ProductDisplay />
      </Row>
      <CustomerDetails
        show={showCustomerDetails}
        handleClose={() => {
          setShowCustomerDetails(false);
          dispatch(fetchEstimates());
        }}
      />
      <PastEstimatesModal
        open={openModal}
        onClose={() => setOpenModal(false)}
      />
      <PDFViewerModal
        visible={!!pdfUrl}
        onCancel={() => setPdfUrl("")}
        pdfUrl={pdfUrl}
        handleEmail={handleEmail}
      />
      <Modal
        title="Save Estimate as Template"
        visible={showTemplateModal}
        onCancel={() => setShowTemplateModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowTemplateModal(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={() => {
              if (!templateName || templateName.trim() === "") {
                notification.warning({
                  message: "Warning",
                  description: "Template name cannot be empty.",
                });
                return;
              }

              axiosInstance
                .post(`/templates/${estimate.id}`, {
                  name: templateName,
                })
                .then(() => {
                  dispatch(fetchTemplates());
                  setShowTemplateModal(false);
                  notification.success({
                    message: "Success",
                    description: "Estimate saved as template successfully.",
                  });
                })
                .catch((error) => {
                  notification.error({
                    message: "Error",
                    description: "Failed to save the estimate as template.",
                  });
                });
            }}
          >
            Save
          </Button>,
        ]}
      >
        <Text>
          Enter template name
        </Text>
        <Input
          style={{
            marginTop: 8
          }}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Enter a template name"
        />
      </Modal>
    </div>
  );
};

export default Calculator;
